import { AxiosRequestConfig } from "axios";
import api from "./api";

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export class BaseService {
  protected endpoint: string;
  private static inflightRequests: Map<string, Promise<any>> = new Map();

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  // JSON.stringify's second argument, when an array, is a property-name
  // ALLOW-LIST applied at every nesting level — not a "sort these keys"
  // instruction. `JSON.stringify(data, Object.keys(data).sort())` therefore
  // only keeps top-level key names and silently drops every nested property
  // whose name isn't itself a top-level key (e.g. `filters.status`,
  // `pagination.page`), collapsing requests like `{filters: {status: ...}}`
  // and `{filters: {status: ..., payingStatus: ...}}` to the same
  // "{"filters":{},"pagination":{}}" key. Two concurrent POSTs with
  // genuinely different bodies then dedupe onto the same in-flight promise,
  // silently serving one request's response for the other's request.
  // This recursively sorts keys at every level so the resulting string is a
  // true, content-sensitive canonical form of `data`.
  private static canonicalStringify(value: any): string {
    if (Array.isArray(value)) {
      return `[${value.map((v) => BaseService.canonicalStringify(v)).join(",")}]`;
    }
    if (value !== null && typeof value === "object") {
      const keys = Object.keys(value).sort();
      const entries = keys.map(
        (k) => `${JSON.stringify(k)}:${BaseService.canonicalStringify(value[k])}`,
      );
      return `{${entries.join(",")}}`;
    }
    return JSON.stringify(value);
  }

  private static makeRequestKey(
    method: string,
    url: string,
    data?: any,
  ): string {
    const sortedData = data ? BaseService.canonicalStringify(data) : "";
    return `${method}:${url}:${sortedData}`;
  }

  private request<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const url = `${this.endpoint}${path}`;
    const key = BaseService.makeRequestKey(method, url, data);

    if (BaseService.inflightRequests.has(key)) {
      return BaseService.inflightRequests.get(key)!;
    }

    let req: Promise<T>;

    switch (method) {
      case "GET":
        req = api.get(url, config).then((res) => res.data);
        break;
      case "POST":
        req = api.post(url, data, config).then((res) => res.data);
        break;
      case "PUT":
        req = api.put(url, data, config).then((res) => res.data);
        break;
      case "PATCH":
        req = api.patch(url, data, config).then((res) => res.data);
        break;
      case "DELETE":
        req = api.delete(url, config).then((res) => res.data);
        break;
      default:
        throw new Error("Unsupported method");
    }

    const wrappedReq = req
      .then((result) => {
        BaseService.inflightRequests.delete(key);
        return result;
      })
      .catch((error) => {
        BaseService.inflightRequests.delete(key);
        throw error;
      });

    BaseService.inflightRequests.set(key, wrappedReq);
    return wrappedReq;
  }

  protected get<T>(path: string = "", config?: AxiosRequestConfig): Promise<T> {
    return this.request("GET", path, undefined, config);
  }

  protected post<T>(
    path: string = "",
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request("POST", path, data, config);
  }

  protected put<T>(
    path: string = "",
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request("PUT", path, data, config);
  }

  protected patch<T>(
    path: string = "",
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request("PATCH", path, data, config);
  }

  protected delete<T>(
    path: string = "",
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request("DELETE", path, undefined, config);
  }
}
